import React, { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import NewIcon from 'ui/atoms/NewIcon/NewIcon'
import Tooltip from 'ui/components/Tooltip/Tooltip'
import styles from './InfoBanner.module.less'

type BaseProps = {
  text: string
  style?: 'info' | 'warning' | 'danger' | 'newFeature'
  'data-testid'?: string
}

type TextVariantProps = BaseProps & {
  variant?: 'text' | 'textWithBackground'
  url?: {
    link: string
    text?: string
    openInNewTab?: boolean
  }
}

type IconVariantProps = BaseProps & {
  variant: 'icon'
  tooltipPlacement?: 'top' | 'bottom' | 'left' | 'right'
  url?: never
}

type InfoBannerProps = TextVariantProps | IconVariantProps

const iconStyles: Record<string, string> = {
  info: 'fas fa-info-circle',
  warning: 'fas fa-info-circle',
  danger: 'fas fa-exclamation-circle',
}

const backgroundStyles: Record<string, string> = {
  info: 'bg-info-2',
  warning: 'bg-warning-3',
  danger: 'bg-danger-2',
  newFeature: 'bg-info-2',
}

export default function InfoBanner({
  text,
  variant = 'textWithBackground',
  style = 'info',
  url,
  'data-testid': dataTestId,
  ...rest
}: InfoBannerProps): ReactElement {
  const hasBackground = variant === 'textWithBackground'
  const tooltipPlacement = 'tooltipPlacement' in rest ? rest.tooltipPlacement : 'top'

  const { t } = useTranslation()

  const ariaLabels: Record<string, string> = {
    info: t('Information'),
    warning: t('Warning'),
    danger: t('Danger'),
  }

  const renderIcon = () => {
    if (style === 'newFeature') {
      return (
        <span className="mr-1">
          <NewFeatureIcon ariaLabel={text} />
        </span>
      )
    }
    const iconClass = hasBackground ? iconStyles[style] : iconStyles[style]
    const iconColor = hasBackground ? 'black' : style
    return (
      <span className="mr-1">
        <NewIcon ariaLabel={ariaLabels[style]} icon={iconClass} color={iconColor} />
      </span>
    )
  }

  if (variant === 'icon') {
    if (style === 'newFeature') {
      return (
        <Tooltip title={text} placement={tooltipPlacement} arrow>
          <span className={styles.InfoBanner_IconWrapper}>
            <NewFeatureIcon ariaLabel={text} iconStyle />
          </span>
        </Tooltip>
      )
    }
    return (
      <Tooltip title={text} placement={tooltipPlacement} arrow>
        <span className={styles.InfoBanner_IconWrapper}>
          <NewIcon ariaLabel={text} icon={iconStyles[style]} color={style} />
        </span>
      </Tooltip>
    )
  }

  const backgroundClass = hasBackground ? `${backgroundStyles[style]} text-black rounded` : ''

  return (
    <div className={`flex flex-col text-sm italic p-1 pr-2 m-1 ${backgroundClass}`} data-testid={dataTestId}>
      <div className="flex items-center whitespace-pre-wrap">
        {renderIcon()}
        {text}
      </div>
      {url && (
        <div className="underline px-5 pt-1">
          <a href={url.link} {...(url.openInNewTab !== false && { target: '_blank', rel: 'noopener noreferrer' })}>
            {url.text || url.link}
          </a>
        </div>
      )}
    </div>
  )
}

function NewFeatureIcon({ ariaLabel, iconStyle = false }: { ariaLabel: string; iconStyle?: boolean }): ReactElement {
  const { t } = useTranslation()
  const newFeatureClass = iconStyle
    ? `${styles.InfoBanner_NewFeature} ${styles.InfoBanner_NewFeatureIconStyle}`
    : styles.InfoBanner_NewFeature

  return (
    <div className={`${styles.InfoBanner_NewFeatureWrapper} not-italic`}>
      <div className={newFeatureClass} aria-label={ariaLabel}>
        {t('NEW!')}
      </div>
    </div>
  )
}
